<template>
  <Teleport to="#teleports">
    <div
      ref="overlayRef"
      class="fixed inset-0 z-50 pt-4 px-4 h-screen w-screen max-h-svh opacity-0 invisible translate-y-full will-change-transform md:pt-8 md:px-8 lg:pt-12 lg:px-12"
      :aria-expanded="overlayState.isOpen ? 'true' : 'false'"
    >
      <div class="fixed inset-0 bg-blue-light opacity-0 invisible" data-gsap-target="background" />

      <div class="relative overflow-hidden flex p-6 h-full w-full bg-white rounded-t-2xl lg:pt-32 lg:rounded-t-4xl xl:px-28">
        <button
          type="button"
          class="btn btn--secondary btn--rounded absolute top-4 right-4 h-10 w-10 after:bg-blue-light hover:border-blue-light md:h-12 md:w-12 lg:top-11 lg:right-11"
          @click="() => toggleOverlay()"
          :aria-label="$statamic.trans('a11y_toggle_overlay')"
          :aria-controls="overlayState.id"
          :aria-expanded="overlayState.isOpen ? 'true' : 'false'"
        >
          <MenuIcon :is-open="overlayState.isOpen" />
        </button>

        <div class="grid gap-8 overflow-y-scroll lg:grid-cols-[1fr,27rem] lg:pb-32 xl:gap-32">
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
const route = useRoute();
const { overlayState, toggleOverlay } = useOverlay();

const props = defineProps<{
  gtmOverlayName: string;
}>();

let ctx: gsap.Context | null;
let timeline: GSAPTimeline;
const overlayRef = ref();

watch(() => route.path, () => {
  toggleOverlay(false);
});

watch(() => overlayState.isOpen, (isOpen) => {
  if (isOpen) {
    timeline.timeScale(1).play();

    useGtm().trackEvent({
      event: 'overlay_opened',
      overlay_name: props.gtmOverlayName ?? 'overlay',
    });
  } else {
    timeline.timeScale(5).reverse();
  }
});

onMounted(() => {
  ctx = useGsap.context((self) => {
    if (typeof self.selector !== 'function') return;

    const gsapBackground = self.selector('[data-gsap-target="background"]');

    timeline = useGsap.timeline({
      paused: true,
    });

    timeline
      .to(overlayRef.value, {
        y: 0,
        autoAlpha: 1,
      })
      .to(gsapBackground, {
        autoAlpha: 1,
      });
  }, overlayRef.value);
});

onUnmounted(() => {
  ctx?.revert();
});
</script>
